import { useCcmDetails } from '../../hooks/store/useCcmDetails';
import { usePatientContacts } from '../../hooks/store/usePatientContacts';
import {
  Avatar,
  Box,
  Divider,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { displayFullName, nameInitials } from '@xolv-mybrightlink/common';
import { CaseManagerDetails } from '../../type';

const ccmPhone = (ccm: CaseManagerDetails, intl: IntlShape): string => {
  const title = intl.formatMessage({ id: 'care-team.ccm.title' });
  return ccm.mobilePhone ? `${title}・${ccm.mobilePhone}` : title;
};

const ccmLanguages = (ccm: CaseManagerDetails): string =>
  ccm.languages.join(', ');

const ccmLicenses = (ccm: CaseManagerDetails): string => {
  return ccm.licenses
    .map((license) => {
      const { licenseDetail, licenseNumber } = license;
      if (!licenseDetail) {
        return '';
      } else if (licenseNumber) {
        return `${licenseDetail}, #${licenseNumber}`;
      } else {
        return licenseDetail;
      }
    })
    .join('. ');
};

const ccmDegrees = (ccm: CaseManagerDetails): string => {
  return ccm.degrees
    .map((degree) => {
      const { degreeDetail, graduatedFrom } = degree;
      if (!degreeDetail) {
        return '';
      } else if (graduatedFrom) {
        return `${degreeDetail}, ${graduatedFrom}`;
      } else {
        return degreeDetail;
      }
    })
    .join('. ');
};

const CcmInfoField = (props: { titleId: string; content: string }) => {
  const intl = useIntl();
  const title = intl.formatMessage({ id: props.titleId });
  if (!props.content) {
    return null;
  }
  return (
    <Box sx={{ px: 4, py: 1 }}>
      <Typography
        component='span'
        sx={{ fontWeight: 600, fontSize: 14 }}
      >{`${title}: `}</Typography>
      <Typography component='span' sx={{ fontSize: 14 }}>
        {props.content}
      </Typography>
    </Box>
  );
};

export const CcmChannelMembers = (props: { channelGuid: string }) => {
  const { channelGuid } = props;
  const intl = useIntl();
  const ccmDetails = useCcmDetails(channelGuid);
  const contacts = usePatientContacts(channelGuid);

  return (
    <>
      <Typography sx={{ fontSize: 14, ml: 5, mt: 5, color: '#626a7a' }}>
        <FormattedMessage id='care-team.members' />
      </Typography>
      {ccmDetails.map((ccm) => {
        return (
          <Box key={ccm.sfId} sx={{ my: 4 }}>
            <ListItem sx={{ mb: 2 }}>
              <ListItemAvatar
                sx={{ minWidth: 'unset', pr: 2, fontWeight: 600 }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#626a7a',
                    fontSize: 16,
                    width: 50,
                    height: 50,
                  }}
                >
                  {nameInitials(ccm)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={displayFullName(ccm)}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  },
                }}
                secondary={ccmPhone(ccm, intl)}
                secondaryTypographyProps={{
                  sx: {
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  },
                }}
              />
            </ListItem>
            <CcmInfoField
              titleId='care-team.ccm.languages'
              content={ccmLanguages(ccm)}
            />
            <CcmInfoField
              titleId='care-team.ccm.licenses'
              content={ccmLicenses(ccm)}
            />
            <CcmInfoField
              titleId='care-team.ccm.degrees'
              content={ccmDegrees(ccm)}
            />
            <Divider sx={{ mt: 5, mx: 4 }} />
          </Box>
        );
      })}
      {contacts.map((contact) => {
        return (
          <ListItem key={contact.guid}>
            <ListItemAvatar sx={{ minWidth: 'unset', pr: 2, fontWeight: 600 }}>
              <Avatar
                sx={{ bgcolor: '#626a7a', fontSize: 14, width: 30, height: 30 }}
              >
                {nameInitials(contact)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography sx={{ fontSize: 14 }}>
                  {displayFullName(contact)}
                </Typography>
              }
            />
          </ListItem>
        );
      })}
    </>
  );
};
